import { useMutation } from '@tanstack/react-query';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageData } from '#src/app/pages/startAPetitionV2/pageContext';
import { inferPetitionScopeLocation } from '#src/app/pages/startAPetitionV2/shared/api';

type Input = { mainPrompt: string; personalStory?: string };

/**
 * The subset of the inference result stashed on the draft (CHANGE-97720) for later emission on
 * `activate_petition`. `city`/`adminArea`/`countryCode` (CHANGE-98274) feed the DMI mutation's
 * `relevantLocation` — both `adminArea` and `countryCode` are real ISO codes per the inference
 * prompt (e.g. `"IL"`, `"US"`), not full names. Still deliberately excludes the model's confidence
 * scores and reasoning.
 */
export type InferredScopeLocation = {
	scope: 'LOCAL' | 'NATIONAL' | 'GLOBAL';
	/** The mutation's `location.name`. */
	location: string | null;
	city: string | null;
	adminArea: string | null;
	countryCode: string | null;
};

/**
 * Background scope/location inference for the SAP AI flow (CHANGE-97331 + CHANGE-97720).
 *
 * Returns `mutateAsync(input)` that fires the `inferPetitionScopeLocation` mutation and resolves with
 * the values to stash on the draft, or `null`. Gated on the `sapScopeLocationInference` FCM (off →
 * resolves `null`). The empty-prompt guard and tracking (`sap_scope_inference_completed` /
 * `sap_scope_inference_error`) live in `api.ts`, the same way `generateAiPetitionDraft` tracks itself.
 *
 * On inference failure the mutation **rejects** — the caller (`useGenerateAiDraft`) settles it via
 * `Promise.allSettled`, so a failure never fails the AI-draft submit. Durable once-per-draft dedup
 * (reusing a prior result instead of re-firing) lives at that call site, which owns the draft state;
 * this hook stays a thin wrapper.
 *
 * Errors aren't reported here on purpose: the middle-end resolver already reports unexpected failures
 * to Sentry/NewRelic, so the front-end only needs the analytics signal, not a second report.
 */
export function useInferScopeLocation(): (input: Input) => Promise<InferredScopeLocation | null> {
	const { data: pageData } = useStartAPetitionPageData();
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const enabled = pageData.featureConfigs.sapScopeLocationInference;

	const { mutateAsync } = useMutation({
		mutationFn: async (input: Input): Promise<InferredScopeLocation | null> => {
			if (!enabled) return null;

			const result = await inferPetitionScopeLocation(input, utilityContext, track);
			if (!result) return null;
			return {
				scope: result.scope,
				location: result.location.name,
				city: result.location.city,
				adminArea: result.location.adminArea,
				countryCode: result.location.countryCode,
			};
		},
	});

	return mutateAsync;
}
